import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

import LoadingIndicator from 'app/components/LoadingIndicator';
import { useStyles } from 'app/pages/DealerSettingsPage/style';
import { MyautoiqSegmentSettingsComponent } from 'app/pages/MyautoiqSegmentSettingsComponent';
import { MyautoiqTemplateGenerationSettingsComponent } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent';
import { MyautoiqNurturingJourneySettingsComponent } from 'app/pages/MyautoiqNurturingJourneySettingsComponent';
import { selectMyautoiqSegmentSettings } from 'app/pages/MyautoiqSegmentSettingsComponent/selectors';
import { selectMyautoiqTemplateGenerationSettings } from 'app/pages/MyautoiqTemplateGenerationSettingsComponent/selectors';
import { selectMyautoiqNurturingJourneySettings } from 'app/pages/MyautoiqNurturingJourneySettingsComponent/selectors';
import { fetchManufacturers } from 'app/pages/MyautoiqSettingsPage/apiService/myautoiqSettingsApiService';

import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

export default function MyautoiqSettingsPage() {
  const classes = useStyles();

  const myautoiqSegmentSettingsDetails = useSelector(
    selectMyautoiqSegmentSettings,
  );
  const myautoiqTemplateGenerationSettingsDetails = useSelector(
    selectMyautoiqTemplateGenerationSettings,
  );
  const myautoiqNurturingJourneySettingsDetails = useSelector(
    selectMyautoiqNurturingJourneySettings,
  );

  const [manufacturers, setManufacturers] = React.useState<any[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = React.useState<any>(null);
  const [loadingManufacturers, setLoadingManufacturers] = React.useState(false);

  // Load manufacturer from localStorage on mount
  React.useEffect(() => {
    const savedManufacturer = localStorage.getItem('selectedManufacturer');
    if (savedManufacturer) {
      try {
        const manufacturerData = JSON.parse(savedManufacturer);
        setSelectedManufacturer(manufacturerData);
      } catch (error) {
        console.error('Error parsing saved manufacturer:', error);
        localStorage.removeItem('selectedManufacturer');
      }
    }
  }, []);

  // Fetch manufacturers list
  React.useEffect(() => {
    const loadManufacturers = async () => {
      setLoadingManufacturers(true);
      try {
        const response = await fetchManufacturers();
        const manufacturersList = response?.body?.carlistDetails || [];
        setManufacturers(manufacturersList);
      } catch (error) {
        console.error('Error loading manufacturers:', error);
        setManufacturers([]);
      } finally {
        setLoadingManufacturers(false);
      }
    };

    loadManufacturers();
  }, []);

  const handleManufacturerChange = (event: any) => {
    const manufacturerId = event.target.value;
    const manufacturer = manufacturers.find(
      (m: any) => m._id === manufacturerId,
    );
    
    if (manufacturer) {
      const manufacturerData = {
        man_id: manufacturer.man_id,
        man_name: manufacturer.man_name,
        _id: manufacturer._id,
      };
      setSelectedManufacturer(manufacturerData);
      localStorage.setItem('selectedManufacturer', JSON.stringify(manufacturerData));
    }
  };

  return (
    <>
      <Helmet>
        <title>Myautoiq Settings</title>
        <meta name="description" content="Myautoiq Settings - Myautoiq" />
      </Helmet>

      {(myautoiqSegmentSettingsDetails.loading ||
        myautoiqTemplateGenerationSettingsDetails.loading ||
        myautoiqNurturingJourneySettingsDetails.loading) && <LoadingIndicator />}

      <div className={classes.root}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="h4"
            gutterBottom
            component="div"
            style={{ display: 'flex', alignItems: 'flex-end' }}
          >
            Myautoiq Settings
            {selectedManufacturer && (
              <Typography
                variant="subtitle1"
                gutterBottom
                component="div"
                style={{ marginLeft: 15 }}
              >
                {`${selectedManufacturer.man_name}(${selectedManufacturer._id})`}
              </Typography>
            )}
          </Typography>

          <FormControl
            sx={{
              minWidth: 250,
              marginRight: '30px',
            }}
          >
            <InputLabel id="manufacturer-select-label">
              Manufacturer
            </InputLabel>
            <Select
              labelId="manufacturer-select-label"
              label="Manufacturer"
              value={selectedManufacturer?._id || ''}
              onChange={handleManufacturerChange}
              disabled={loadingManufacturers}
            >
              {loadingManufacturers ? (
                <MenuItem disabled>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <span>Loading...</span>
                  </Box>
                </MenuItem>
              ) : manufacturers.length === 0 ? (
                <MenuItem disabled>No manufacturers available</MenuItem>
              ) : (
                manufacturers.map((manufacturer: any) => (
                  <MenuItem 
                    key={manufacturer._id} 
                    value={manufacturer._id}
                  >
                    {manufacturer.man_name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </div>

        {!selectedManufacturer ? (
          <Typography
            variant="h6"
            gutterBottom
            component="div"
            className={classes.dealerTypography}
            sx={{ textAlign: 'center', marginTop: 4, color: 'text.secondary' }}
          >
            Please select a manufacturer to load settings
          </Typography>
        ) : (
          <>
            <MyautoiqSegmentSettingsComponent manufacturerId={selectedManufacturer._id} />
            <MyautoiqTemplateGenerationSettingsComponent manufacturerId={selectedManufacturer._id} />
            <MyautoiqNurturingJourneySettingsComponent manufacturerId={selectedManufacturer._id} />
          </>
        )}
      </div>
    </>
  );
}
